import { ICycleSession } from 'pages/Planning/types/cycleSession'
import React, { useState, useMemo, useCallback, useRef } from 'react'
import { formatHours, getStatusLabel } from 'pages/Planning/utils/helpers'
import ProgressCircle from 'components/ProgressCircle'
import { AiOutlineSearch, AiOutlineCaretDown } from 'react-icons/ai'
import { TbClockPause, TbCircleCheck, TbClockPlay } from 'react-icons/tb'
import { useDispatch, useSelector } from 'react-redux'
import { useTracking } from 'react-tracking'
import _ from 'lodash'
import { BsArrowRight } from 'react-icons/bs'
import history from 'services/history'
import { learningApi } from 'services/api'
import { RootState } from 'store/modules/rootReducer'
import { editPreference } from 'store/modules/student/actions'
import { TrackingSchemaTypes } from 'tracking'
import { IAccessContentsRes } from 'pages/Course/components/ModuleAcordeon/types'
import { IStudyPlan } from 'store/modules/studyPlan/types'
import { toast } from 'react-toastify'
import { planningApi } from 'pages/Planning/services/api'
import { useClickOutside } from 'hooks/use-click-outside'
import { MdLock } from 'react-icons/md'
import { useFreemiumModal } from 'hooks/use-freemium-modal'
import StartTimerButton from 'components/Timer/StartTimerButton'
import { useRestartCycleTour } from 'pages/Planning/hooks/useRestartCycleTour'
interface CurrentSessionProps {
  current: ICycleSession
  content: any
  plan: IStudyPlan
  course: any
  isLoading?: boolean
}

type ProgressFilter = 'all' | 'in-progress' | 'completed'

const CurrentSession: React.FC<CurrentSessionProps> = ({
  current,
  content,
  plan,
  course,
  isLoading,
}) => {
  const [headingFilter, setHeadingFilter] = useState('')
  const [progressFilter, setProgressFilter] = useState<ProgressFilter>('all')
  const [loadingLessonRef, setLoadingLessonRef] = useState<string | null>(null)
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false)
  const [updatingStatus, setUpdatingStatus] = useState(false)
  const dispatch = useDispatch()
  const { profile } = useSelector((state: RootState) => state.student)
  const { trackEvent } = useTracking()
  const { token } = useSelector((state: RootState) => state.auth)
  const { isDarkMode } = useSelector((state: RootState) => state.theme)

  // Hook para gerenciar o mini-tour do botão Reiniciar Ciclo
  useRestartCycleTour({ isDarkMode })

  // Hook para gerenciar o modal do freemium
  const freemiumModal = useFreemiumModal({
    course,
    moduleId: current?.module?._id,
  })

  // Fechar dropdown ao clicar fora
  const statusDropdownRef = useClickOutside<HTMLDivElement>(() => {
    if (statusDropdownOpen) {
      setStatusDropdownOpen(false)
    }
  })

  // Calcular contagens para cada filtro de progresso
  const progressCounts = useMemo(() => {
    const children = content?.children || []
    const total = children.length
    const inProgress = children.filter(
      (child: any) => (child.progress || 0) > 0 && !child.completed
    ).length
    const completed = children.filter((child: any) => child.completed === true).length

    return {
      all: total,
      'in-progress': inProgress,
      completed: completed,
    }
  }, [content?.children])

  // Filtrar children baseado nos filtros
  const filteredChildren = useMemo(() => {
    let children = content?.children || []

    // Filtrar por heading
    if (headingFilter.trim()) {
      const searchTerm = headingFilter.toLowerCase().trim()
      children = children.filter((child: any) => child.heading?.toLowerCase().includes(searchTerm))
    }

    // Filtrar por progresso
    if (progressFilter === 'in-progress') {
      children = children.filter((child: any) => (child.progress || 0) > 0 && !child.completed)
    } else if (progressFilter === 'completed') {
      children = children.filter((child: any) => child.completed === true)
    }

    return children
  }, [content?.children, headingFilter, progressFilter])

  const handleHeadingFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHeadingFilter(e.target.value)
  }

  const handleProgressFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setProgressFilter(e.target.value as ProgressFilter)
  }

  const handleContinue = useCallback(
    (item: IAccessContentsRes['lastViewed']) => {
      if (!course) return

      const m = _.sortBy(course.children, 'order').find((t) => t.ref === item.module._id)
      const l = course.children
        .flatMap((t) =>
          t.children.flatMap((u) => ({ ref: u.ref, name: u.name, heading: u.heading }))
        )
        .find((t) => t.ref === item.lesson._id)

      if (!m || !l) return

      const total = content?.children?.length || 0
      const current = _.sortBy(m.children, 'order').findIndex((t) => t.ref === l.ref)

      const partitions = _.chunk(
        new Array(total).fill(1).map((t, i) => i),
        Math.ceil(total / 3)
      )
      const [Inicio, Meio = false, Fim = false] = partitions.map((t) => t.includes(current))

      trackEvent({
        event: TrackingSchemaTypes.AccessContent,
        properties: {
          Carreira: plan.publicTender?.career || course.career || '',
          Instituicao: plan.publicTender?.name || course.publicTender || '',
          Curso: course.name,
          Modulo: m.subject || m.name,
          Aula: l.heading || l.name,
          Capitulo: item.chapter.name,
          Formato: profile.preference === 0 ? 'Excerto' : 'Vídeo',
          Modo: 'Continuar',
          Atual: current + 1,
          Total: m.children.length,
          Porcentagem: course?.progress || 0,
          Inicio,
          Meio,
          Fim,
          Origem: 'Sessão de Estudo',
        },
      })

      if (item && item.object.type) dispatch(editPreference(item.object.type !== 'video' ? 1 : 0))

      const url = `/course/${item.course._id}/module/${item.module._id}/lesson/${item.lesson._id}/chapter/${item.chapter._id}`
      return window.open(url, '_blank')
    },
    [course, content, plan, profile, trackEvent, dispatch]
  )

  const handleInit = useCallback(
    (item: IAccessContentsRes['contents'][number]['data']) => {
      if (!course || !item) return

      const m = _.sortBy(course.children, 'order').find((t) => t.ref === item.module._id)
      const l = course.children
        .flatMap((t) =>
          t.children.flatMap((u) => ({ ref: u.ref, name: u.name, heading: u.heading }))
        )
        .find((t) => t.ref === item.lesson._id)

      if (!m || !l) return

      const total = content?.children?.length || 0
      const current = _.sortBy(m.children, 'order').findIndex((t) => t.ref === l.ref)

      const partitions = _.chunk(
        new Array(total).fill(1).map((t, i) => i),
        Math.ceil(total / 3)
      )
      const [Inicio, Meio = false, Fim = false] = partitions.map((t) => t.includes(current))

      trackEvent({
        event: TrackingSchemaTypes.AccessContent,
        properties: {
          Carreira: plan.publicTender?.career || course.career || '',
          Instituicao: plan.publicTender?.name || course.publicTender || '',
          Curso: course.name,
          Modulo: m.subject || m.name,
          Aula: l.heading || l.name,
          Capitulo: item.chapter.name,
          Formato: profile.preference === 0 ? 'Excerto' : 'Vídeo',
          Modo: 'Começar',
          Atual: current + 1,
          Total: total,
          Porcentagem: course?.progress || 0,
          Inicio,
          Meio,
          Fim,
          Origem: 'Sessão de Estudo',
        },
      })

      if (item?.objects.every((o) => o.match(/video/gi))) dispatch(editPreference(0))
      if (item?.objects.every((o) => o.match(/excerto/gi))) dispatch(editPreference(1))

      const url = `/course/${item?.course._id}/module/${item?.module._id}/lesson/${item?.lesson._id}/chapter/${item?.chapter._id}`
      return window.open(url, '_blank')
    },
    [course, content, plan, profile, trackEvent, dispatch]
  )

  const handleAccessContent = useCallback(
    async (lessonRef: string) => {
      if (!plan.course?._id || !current?.module?._id) return

      setLoadingLessonRef(lessonRef)

      try {
        const headers = {
          Authorization: `Bearer ${token}`,
        }
        const { data } = await learningApi.get<IAccessContentsRes>(
          `/course/${plan.course._id}/module/${current.module._id}/access-content`,
          { headers }
        )

        const contentExists = data?.lastViewed && data?.lastViewed?.lesson?._id === lessonRef

        if (contentExists && data.lastViewed) {
          handleContinue(data.lastViewed)
        } else {
          const content = data.contents.find(
            (content) => content.hasContent && content.data?.lesson._id === lessonRef
          )?.data
          if (content) {
            handleInit(content)
          }
        }
      } catch (err) {
        toast.error('Ocorreu um erro ao carregar o conteúdo da aula')
      } finally {
        setLoadingLessonRef(null)
      }
    },
    [plan.course?._id, current?.module?._id, token, handleContinue, handleInit]
  )

  const handleStatusChange = useCallback(
    async (newStatus: 'pending' | 'in_progress' | 'done') => {
      if (!plan._id || !current?._id || updatingStatus) return

      setUpdatingStatus(true)
      setStatusDropdownOpen(false)

      try {
        await planningApi.patchCycleSession(plan._id, current._id, newStatus)
        toast.success('Status atualizado com sucesso')
        // Recarregar a página ou atualizar o estado local
        window.location.reload()
      } catch (err: any) {
        toast.error(err?.response?.data?.message || 'Erro ao atualizar status')
      } finally {
        setUpdatingStatus(false)
      }
    },
    [plan._id, current?._id, updatingStatus]
  )

  const handleStatusDropdownToggle = useCallback(() => {
    setStatusDropdownOpen(!statusDropdownOpen)
  }, [statusDropdownOpen])

  const handleRestartCycle = useCallback(() => {
    planningApi
      .restartCycle(plan._id)
      .then(() => {
        toast.success('Ciclo reiniciado com sucesso')
        window.location.reload()
      })
      .catch((err: any) => {
        toast.error(err?.response?.data?.message || 'Erro ao reiniciar ciclo')
      })
  }, [plan._id])

  return (
    <div className="cycle-current-session">
      {current?._id ? (
        <>
          <div
            key={current._id}
            className={`cycle-activity-item ${current?.status === 'done' ? 'completed' : ''}`}
            style={{ backgroundColor: current?.color }}
            data-tour="dashboard-current-session"
          >
            <div className="cycle-activity-session-subject">
              <span className="cycle-activity-subject">
                {current.order.toString().padStart(2, '0')}ª / {current.module.subject}
              </span>
              <span className="cycle-activity-session-hours">
                Duração: {formatHours(current.hours)}
              </span>
            </div>

            <div className="cycle-activity-content">
              <div className="cycle-activity-meta">
                <div data-tour="dashboard-start-timer">
                  <StartTimerButton />
                </div>
                <div
                  className="cycle-activity-status-dropdown"
                  ref={statusDropdownRef}
                  data-open={statusDropdownOpen}
                  data-tour="dashboard-session-status"
                >
                  <button
                    className="cycle-activity-status-button"
                    onClick={handleStatusDropdownToggle}
                    disabled={updatingStatus}
                  >
                    {updatingStatus ? (
                      'Atualizando...'
                    ) : current.status === 'done' ? (
                      <>
                        <TbCircleCheck size={18} />
                        Concluída
                      </>
                    ) : current.status === 'in_progress' ? (
                      <>
                        <TbClockPlay size={18} />
                        Em andamento
                      </>
                    ) : (
                      <>
                        <TbClockPause size={18} />
                        Pendente
                      </>
                    )}
                    <AiOutlineCaretDown
                      size={12}
                      style={{
                        marginLeft: '0.25rem',
                        transition: 'transform 0.2s ease',
                        transform: statusDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      }}
                    />
                  </button>
                  {statusDropdownOpen && (
                    <div className="cycle-activity-status-menu">
                      <ul>
                        <li
                          onClick={() => handleStatusChange('pending')}
                          className={current.status === 'pending' ? 'active' : ''}
                        >
                          <TbClockPause size={18} />
                          Pendente
                        </li>
                        <li
                          onClick={() => handleStatusChange('in_progress')}
                          className={current.status === 'in_progress' ? 'active' : ''}
                        >
                          <TbClockPlay size={18} />
                          Em andamento
                        </li>
                        <li
                          onClick={() => handleStatusChange('done')}
                          className={current.status === 'done' ? 'active' : ''}
                          data-tour="dashboard-complete-session"
                        >
                          <TbCircleCheck size={18} />
                          Concluída
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="cycle-current-session-filters" data-tour="dashboard-filters">
            <div className="cycle-filter-group cycle-filter-group-aulas">
              <label htmlFor="headingFilter" className="cycle-filter-label">
                Aulas
              </label>
              <div className="cycle-filter-input-wrapper">
                <AiOutlineSearch className="cycle-filter-search-icon" size={18} />
                <input
                  type="text"
                  id="headingFilter"
                  className="cycle-filter-input"
                  placeholder="Buscar por nome da aula..."
                  value={headingFilter}
                  onChange={handleHeadingFilterChange}
                />
              </div>
            </div>

            <div className="cycle-filter-group cycle-filter-group-progresso">
              <label htmlFor="progressFilter" className="cycle-filter-label">
                Progresso
              </label>
              <select
                id="progressFilter"
                className="cycle-filter-select"
                value={progressFilter}
                onChange={handleProgressFilterChange}
              >
                <option value="all">Todas ({progressCounts.all})</option>
                <option value="in-progress">Em andamento ({progressCounts['in-progress']})</option>
                <option value="completed">Concluídas ({progressCounts.completed})</option>
              </select>
            </div>
          </div>
          <div className="cycle-activities-list">
            <div className="cycle-current-session-content" data-tour="dashboard-lessons-list">
              {isLoading ? (
                // Skeleton loading
                Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="cycle-current-session-child">
                    <div className="cycle-current-session-child-content">
                      <div className="cycle-current-session-child-progress">
                        <div
                          className="loading-block"
                          style={{ width: 40, height: 40, borderRadius: '50%' }}
                        />
                      </div>
                      <div className="cycle-current-session-child-header">
                        <div
                          className="loading-block"
                          style={{ width: 60, height: 14, marginBottom: 6 }}
                        />
                        <div className="loading-block" style={{ width: 200, height: 18 }} />
                      </div>
                    </div>
                    <div className="cycle-current-session-child-actions">
                      <div
                        className="loading-block"
                        style={{ width: 100, height: 36, borderRadius: 6 }}
                      />
                    </div>
                  </div>
                ))
              ) : filteredChildren.length > 0 ? (
                filteredChildren.map((child: any) => (
                  <div key={child.ref} className="cycle-current-session-child">
                    <div className="cycle-current-session-child-content">
                      <div className="cycle-current-session-child-progress">
                        <ProgressCircle percentage={child.progress || 0} />
                      </div>
                      <div className="cycle-current-session-child-header">
                        <div className="cycle-current-session-child-subtitle">
                          Aula {(child.order + 1).toString().padStart(2, '0')}
                        </div>
                        <div className="cycle-current-session-child-title">
                          <div className="cycle-current-session-child-title-text">
                            {child.heading}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="cycle-current-session-child-actions">
                      <button
                        className="cycle-session-button"
                        onClick={() => {
                          if (child.available.lock) {
                            freemiumModal.openModal()
                            return
                          }
                          handleAccessContent(child.ref)
                        }}
                        disabled={loadingLessonRef === child.ref}
                      >
                        {loadingLessonRef === child.ref ? (
                          'Carregando...'
                        ) : child.available.lock ? (
                          <>
                            Acessar <MdLock size={18} />
                          </>
                        ) : child.progress > 0 ? (
                          <>
                            Continuar <BsArrowRight size={18} />
                          </>
                        ) : (
                          <>
                            Começar <BsArrowRight size={18} />
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="cycle-filter-no-results">
                  <p>Nenhuma aula encontrada com os filtros aplicados.</p>
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="cycle-current-session-empty">
          <h3>Você concluiu todas as disciplinas deste ciclo.</h3>
          <p>Reinicie o ciclo para prosseguir com a sua jornada de estudos.</p>
          <div className="cycle-current-session-empty-actions">
            <button
              className="cycle-session-button primary"
              onClick={() => handleRestartCycle()}
              data-tour="dashboard-restart-cycle"
            >
              Reiniciar ciclo
            </button>
          </div>
        </div>
      )}
      {freemiumModal.renderModal()}
    </div>
  )
}

export default CurrentSession
